import React from 'react';
import Image from 'next/image';
import { Stack, Typography, useTheme } from '@mui/material';
import { Box } from '@mui/system';

import { Container } from '@/components/layout';
import Icon from '@/components/icons/Icon';

const DATA = [
  {
    image: {
      src: '/images/press-center/aarp.png',
    },
    title: 'OneVillage joins AgeTech Collaborative™ from AARP',
    description: 'OneVillage provide personalized and data-driven support, empowering individuals to navigate health challenges with confidence.',
    link: {
      url: 'https://agetechcollaborative.org',
      title: 'agetechcollaborative.org',
    },
  },
  {
    image: {
      src: '/images/press-center/techstars.png',
    },
    title: 'OneVillage joins Techstars NYC accelerator program',
    description: 'OneVillage, a D.C.-based startup, is part of Techstars NYC’s global accelerator class, offering online tools to help patients and families manage care and stay connected.',
    link: {
      url: 'https://techcrunch.com/2021/03/22/techstars-nyc-2021/',
      title: 'techcrunch.com',
    },
  },
  {
    image: {
      src: '/images/press-center/business-journals.png',
    },
    title: 'OneVillage simplifying life for patients and families',
    description: 'OneVillage offers online tools to help patients and families manage care, stay connected, and ease daily challenges.',
    link: {
      url: 'https://www.bizjournals.com/washington/inno/stories/inno-insights/2022/05/23/the-pitch-onevillage-cancer-care-startup-patients.html',
      title: 'bizjournals.com',
    },
  },
];

function HighlightItem({
  link,
  image,
  title,
  description,
}: {
  key: string;
  link: {
    url: string;
    title: string;
  }
  image: {
    src: string;
  };
  title: string;
  description: string;
}) {
  const theme = useTheme();

  return (
    <Stack
      sx={{
        flex: 1,
        overflow: 'hidden',
        paddingRight: { xs: 0, lg: theme.spacing(6) },
        borderRight: { xs: 'none', lg: '1px solid #B0B2BB' },
        paddingBottom: { xs: theme.spacing(6), lg: 0 },
        borderBottom: { xs: '1px solid #B0B2BB', lg: 'none' },

        '&:last-child': {
          paddingRight: 0,
          paddingBottom: 0,
          borderBottomWidth: 0,
          borderRightWidth: 0,
        },
      }}
    >
      <Box
        width={theme.spacing(55)}
        height={theme.spacing(25.5)}
        position="relative"
        sx={{
          marginInline: 'auto',
        }}
      >
        <Image
          src={image.src}
          layout="fill"
          objectFit="cover"
          objectPosition="center"
        />
      </Box>

      <Stack
        sx={{
          gap: theme.spacing(3.5),
          marginTop: theme.spacing(4),
          marginBottom: theme.spacing(5),
        }}
      >
        <Typography
          fontSize="1.4375rem !important"
          lineHeight="1.4 !important"
          variant="h3"
          color="#52545F"
          textAlign="center"
        >
          {title}
        </Typography>

        <Typography
          fontSize="1.125rem !important"
          lineHeight="1.46 !important"
          variant="body2"
          color="#52545F"
          textAlign="center"
        >
          {description}
        </Typography>
      </Stack>

      <Stack
        component="a"
        href={link.url}
        target="_blank"
        rel="noreferrer noopener"
        gap={theme.spacing(1)}
        alignSelf="center"
        alignItems="center"
        flexDirection="row"
      >
        <Icon
          iconName="link-bold"
          style={{ width: '1.125rem', height: '1.125rem' }}
        />

        <Typography
          component="span"
          fontSize="1rem !important"
          lineHeight="1.46 !important"
          variant="body2"
          color="#52545F"
          sx={{
            textDecoration: 'underline',
            textDecorationThickness: '0.05em',
            textUnderlineOffset: '0.0625em',
          }}
        >
          {link.title}
        </Typography>
      </Stack>
    </Stack>
  );
}

function PressHighlights() {
  const theme = useTheme();

  return (
    <Stack
      sx={{
        gap: theme.spacing(8),
        paddingTop: { xs: theme.spacing(8), lg: theme.spacing(10) },
        paddingBottom: { xs: theme.spacing(14), lg: theme.spacing(15) },
        backgroundColor: theme.palette.grey[100],
      }}
    >
      <Typography
        fontSize="2.375rem !important"
        lineHeight="1.2 !important"
        variant="h2"
        color="#82848E"
        textAlign="center" 
      >
        Press Highlights
      </Typography>

      <Container>
        <Stack
          sx={{
            flexDirection: { xs: 'column', lg: 'row' },
            flexWrap: 'wrap',
            rowGap: { xs: theme.spacing(6), lg: theme.spacing(8)},
            columnGap: theme.spacing(6),
            justifyContent: 'center',
          }}
        >
          {DATA.map((item) => (
            <HighlightItem key={item.title} {...item} />
          ))}
        </Stack>
      </Container>
    </Stack>
  );
}

export default PressHighlights;
